// Nether portals (DIM-2, E-11). PURE (ENG-1): every function takes a block
// accessor (get/set/isSolid) + a small palette of the relevant state ids, so the
// same code runs against a live World, the gen worker, the server, and a fixture
// world in tests. Covers frame detection (2×3..21×21, missing-corner-OK / hole-
// fails), interior fill, the overworld↔nether ⅛/×8 coordinate map, and building a
// fresh 4×5 frame with a carved obsidian platform when the target is obstructed.
import { PORTAL, WORLD, NETHER } from '../config';
import { STATE_AIR } from '../engine/section';

export type DimId = 'overworld' | 'nether' | 'end';

/** The block-state ids the portal code reads/writes. */
export interface PortalBlocks {
  air: number;
  obsidian: number;
  portalX: number; // nether_portal[axis=x]
  portalZ: number; // nether_portal[axis=z]
}

/** Block accessor the portal code works over (a live World satisfies it). */
export interface PortalAccess {
  get(x: number, y: number, z: number): number;
  set(x: number, y: number, z: number, state: number): void;
  isSolid(x: number, y: number, z: number): boolean;
}

export type Vec3 = readonly [number, number, number];

/** A detected/validated portal frame interior. */
export interface PortalFrame {
  axis: 'x' | 'z';
  /** Interior cells (portal blocks go here). */
  cells: Vec3[];
  /** Plane-fixed coordinate: z when axis=x, x when axis=z. */
  fixed: number;
  /** Interior span along the horizontal axis. */
  a0: number;
  a1: number;
  /** Interior vertical span. */
  y0: number;
  y1: number;
}

const isCandidate = (s: number, b: PortalBlocks): boolean =>
  s === b.air || s === STATE_AIR || s === b.portalX || s === b.portalZ;
const isObsidian = (s: number, b: PortalBlocks): boolean => s === b.obsidian;
const portalStateFor = (axis: 'x' | 'z', b: PortalBlocks): number => (axis === 'x' ? b.portalX : b.portalZ);

/** A plane cell → world coords, for a given axis and fixed plane coordinate. */
function toWorld(axis: 'x' | 'z', fixed: number, a: number, y: number): Vec3 {
  return axis === 'x' ? [a, y, fixed] : [fixed, y, a];
}

/** Try to detect a valid frame through (sx,sy,sz) in the given plane axis. */
function detectAxis(access: PortalAccess, b: PortalBlocks, sx: number, sy: number, sz: number, axis: 'x' | 'z'): PortalFrame | null {
  const fixed = axis === 'x' ? sz : sx;
  const startA = axis === 'x' ? sx : sz;
  const at = (a: number, y: number): number => {
    const [x, yy, z] = toWorld(axis, fixed, a, y);
    return access.get(x, yy, z);
  };
  if (!isCandidate(at(startA, sy), b)) return null;

  // Flood-fill the connected interior candidates in the plane, bounded by obsidian.
  const visited = new Set<string>();
  const stack: Array<[number, number]> = [[startA, sy]];
  visited.add(`${startA},${sy}`);
  let minA = startA, maxA = startA, minY = sy, maxY = sy;
  const STEPS: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  while (stack.length) {
    const [a, y] = stack.pop()!;
    for (const [da, dy] of STEPS) {
      const na = a + da, ny = y + dy;
      const key = `${na},${ny}`;
      if (visited.has(key)) continue;
      const s = at(na, ny);
      if (isObsidian(s, b)) continue; // frame boundary
      if (!isCandidate(s, b)) return null; // leaked into a wall block → not a frame
      visited.add(key);
      minA = Math.min(minA, na); maxA = Math.max(maxA, na);
      minY = Math.min(minY, ny); maxY = Math.max(maxY, ny);
      if (maxA - minA + 1 > PORTAL.MAX_WIDTH || maxY - minY + 1 > PORTAL.MAX_HEIGHT) return null;
      stack.push([na, ny]);
    }
  }

  const w = maxA - minA + 1, h = maxY - minY + 1;
  if (w < PORTAL.MIN_WIDTH || h < PORTAL.MIN_HEIGHT) return null;
  // The interior must be a filled rectangle (no stray blocks inside).
  for (let a = minA; a <= maxA; a++) {
    for (let y = minY; y <= maxY; y++) if (!isCandidate(at(a, y), b)) return null;
  }
  // Frame ring must be obsidian along each edge (corners exempt — "missing corner OK").
  for (let y = minY; y <= maxY; y++) {
    if (!isObsidian(at(minA - 1, y), b) || !isObsidian(at(maxA + 1, y), b)) return null;
  }
  for (let a = minA; a <= maxA; a++) {
    if (!isObsidian(at(a, minY - 1), b) || !isObsidian(at(a, maxY + 1), b)) return null;
  }

  const cells: Vec3[] = [];
  for (let a = minA; a <= maxA; a++) for (let y = minY; y <= maxY; y++) cells.push(toWorld(axis, fixed, a, y));
  return { axis, cells, fixed, a0: minA, a1: maxA, y0: minY, y1: maxY };
}

/** Detect a valid nether-portal frame through an interior cell, or null. */
export function detectFrame(access: PortalAccess, b: PortalBlocks, x: number, y: number, z: number): PortalFrame | null {
  return detectAxis(access, b, x, y, z, 'x') ?? detectAxis(access, b, x, y, z, 'z');
}

/** Fill a frame's interior with the axis-appropriate portal block. */
export function fillPortal(access: PortalAccess, b: PortalBlocks, frame: PortalFrame): void {
  const state = portalStateFor(frame.axis, b);
  for (const [x, y, z] of frame.cells) access.set(x, y, z, state);
}

/** Light a portal (detect + fill). Returns the frame on success, else null. */
export function ignitePortal(access: PortalAccess, b: PortalBlocks, x: number, y: number, z: number): PortalFrame | null {
  const frame = detectFrame(access, b, x, y, z);
  if (frame) fillPortal(access, b, frame);
  return frame;
}

/** Map one horizontal coordinate between dimensions (⅛ down, ×8 up). */
export function mapCoordinate(v: number, from: DimId, to: DimId): number {
  if (from === to) return v;
  if (from === 'overworld' && to === 'nether') return Math.floor(v / PORTAL.COORD_RATIO);
  return v * PORTAL.COORD_RATIO; // nether → overworld
}

/** Map an (x,y,z) position between dimensions; y is preserved (clamped for nether). */
export function mapPosition(x: number, y: number, z: number, from: DimId, to: DimId): Vec3 {
  const mx = mapCoordinate(Math.floor(x), from, to);
  const mz = mapCoordinate(Math.floor(z), from, to);
  let my = Math.floor(y);
  if (to === 'nether') my = Math.min(my, NETHER_SPAWN_CEIL);
  return [mx, my, mz];
}

/** Highest Y a nether spawn/build may use (below the bedrock roof). */
export const NETHER_SPAWN_CEIL = NETHER.HEIGHT - 8; // 120

/** Search a portal cache for an existing portal within `radius` columns of a
 *  mapped point; returns the nearest one, or null. */
export function findNearestPortal(cache: readonly Vec3[], x: number, y: number, z: number, radius: number): Vec3 | null {
  let best: Vec3 | null = null;
  let bestD = Infinity;
  for (const p of cache) {
    if (Math.abs(p[0] - x) > radius || Math.abs(p[2] - z) > radius) continue;
    const d = (p[0] - x) ** 2 + (p[1] - y) ** 2 + (p[2] - z) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

/** Build a fresh 4×5 obsidian portal (interior 2×3, axis=x) at (tx,ty,tz),
 *  carving an air pocket + obsidian platform first so it is always safe (E-11).
 *  Returns the lit frame and the arrival feet position (interior, on the floor). */
export function buildPortal(access: PortalAccess, b: PortalBlocks, tx: number, ty: number, tz: number): { frame: PortalFrame; arrival: Vec3 } {
  const ix0 = tx, iz = tz;
  // Keep the whole 5-tall frame inside the world.
  const iy0 = Math.max(WORLD.MIN_Y + 2, Math.min(ty, WORLD.MAX_Y - 4));

  // 1. Carve a safe pocket around the frame + standing area (E-11 obstruction).
  for (let x = ix0 - 1; x <= ix0 + 2; x++) {
    for (let y = iy0 - 1; y <= iy0 + 4; y++) {
      for (let z = iz - 1; z <= iz + 1; z++) access.set(x, y, z, b.air);
    }
  }
  // 2. Obsidian platform under the footprint so nothing can fall through.
  for (let x = ix0 - 1; x <= ix0 + 2; x++) {
    for (let z = iz - 1; z <= iz + 1; z++) access.set(x, iy0 - 1, z, b.obsidian);
  }
  // 3. Obsidian frame ring (axis=x, interior x=ix0..ix0+1, y=iy0..iy0+2, z=iz).
  for (let x = ix0 - 1; x <= ix0 + 2; x++) {
    access.set(x, iy0 - 1, iz, b.obsidian);
    access.set(x, iy0 + 3, iz, b.obsidian);
  }
  for (let y = iy0 - 1; y <= iy0 + 3; y++) {
    access.set(ix0 - 1, y, iz, b.obsidian);
    access.set(ix0 + 2, y, iz, b.obsidian);
  }
  // 4. Light the interior.
  const frame = detectFrame(access, b, ix0, iy0, iz);
  const filled = frame ?? {
    axis: 'x' as const, fixed: iz, a0: ix0, a1: ix0 + 1, y0: iy0, y1: iy0 + 2,
    cells: [
      [ix0, iy0, iz], [ix0, iy0 + 1, iz], [ix0, iy0 + 2, iz],
      [ix0 + 1, iy0, iz], [ix0 + 1, iy0 + 1, iz], [ix0 + 1, iy0 + 2, iz],
    ] as Vec3[],
  };
  fillPortal(access, b, filled);
  return { frame: filled, arrival: [ix0 + 1, iy0, iz + 0.5] };
}
